import {
  Check,
  ExternalLink,
  PanelRight,
  Phone,
  PhoneMissed,
  SkipForward,
  StickyNote,
  UserPlus,
  X,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { formatDate } from "../gulaConstants";
import { ClientNotes } from "../notes/ClientNotes";
import type { ContactNotes } from "../notes/clientNotes";
import { NotePreview } from "../notes/NotePreview";
import { currentCallSlot } from "../useAgents";
import {
  CLOSE_CHANCE_LABEL,
  bucketTitle,
  closeChance,
  isTheirTime,
  missionName,
  type CallMission,
} from "./callPlan";
import type { QuickResult } from "./useCallPlan";
import { WhatsappCallButton } from "../mobile/WhatsappCallButton";

const CHANCE_STYLE = {
  high: "bg-red-500 text-white",
  medium: "bg-amber-300 text-neutral-900",
  low: "bg-muted text-muted-foreground",
} as const;

const TEMP_DOT: Record<string, string> = {
  hot: "bg-red-500",
  warm: "bg-amber-400",
  cold: "bg-sky-400",
};

/** Temperature, slot, place, attempts and recontact date in one line. */
export const MissionDetails = ({ mission }: { mission: CallMission }) => (
  <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-neutral-600 dark:text-neutral-400">
    <span
      className={cn(
        "h-2 w-2 rounded-full",
        TEMP_DOT[mission.lead_temperature ?? ""] ?? "bg-neutral-300",
      )}
    />
    <span
      className={cn(
        "rounded-full px-1.5 font-semibold",
        CHANCE_STYLE[closeChance(mission.bucket, mission.lead_temperature)],
      )}
    >
      {
        CLOSE_CHANCE_LABEL[
          closeChance(mission.bucket, mission.lead_temperature)
        ]
      }
    </span>
    {mission.call_slot ? (
      <span>{mission.call_slot.split(",").join(" · ")} h</span>
    ) : null}
    {mission.call_slot && currentCallSlot() ? (
      isTheirTime(mission.call_slot, currentCallSlot()) ? (
        <span className="rounded-full bg-lime-300 px-1.5 font-medium text-neutral-900">
          Es su hora
        </span>
      ) : (
        <span className="rounded-full bg-muted px-1.5">Fuera de su hora</span>
      )
    ) : null}
    {mission.city || mission.zone ? (
      <span>{mission.city ?? mission.zone}</span>
    ) : null}
    {mission.call_attempts ? (
      <span>
        {mission.call_attempts}{" "}
        {mission.call_attempts === 1 ? "intento" : "intentos"}
      </span>
    ) : null}
    {mission.stage === "recontact" && mission.recontact_at ? (
      <span>recontactar {formatDate(mission.recontact_at)}</span>
    ) : null}
  </span>
);

/** The next call, big, with the one-click results. */
export const NextCall = ({
  mission,
  busy,
  onResult,
  onRelease,
  notes,
  onNotesChanged,
  onOpenFicha,
  fichaOpen,
  onAssign,
}: {
  mission: CallMission;
  busy: boolean;
  onResult: (mission: CallMission, result: QuickResult) => void;
  /** Admins: pass the lead to someone for a day */
  onAssign?: (mission: CallMission) => void;
  /** Gives the lead back to the pool (not counted as a call) */
  onRelease?: (mission: CallMission) => void;
  /** Team notes of this client, shown big to read before calling */
  notes?: ContactNotes;
  onNotesChanged?: () => void;
  /** Opens the client panel next to the list (otherwise goes to Clientes) */
  onOpenFicha?: (mission: CallMission) => void;
  /** The client panel of this call is open */
  fichaOpen?: boolean;
}) => {
  const isMobile = useIsMobile();
  return (
    <div className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm dark:bg-neutral-900">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
          Siguiente llamada
        </span>
        <span className="rounded-full bg-lime-100 px-2 py-0.5 text-xs font-medium text-lime-900 dark:bg-lime-300/15 dark:text-lime-200">
          {bucketTitle(mission.bucket)}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-2xl font-semibold leading-tight text-neutral-900 dark:text-neutral-50">
          {missionName(mission)}
        </span>
        <MissionDetails mission={mission} />
      </div>
      {notes && onNotesChanged ? (
        <ClientNotes
          key={String(mission.contact_id)}
          contactId={mission.contact_id}
          notes={notes}
          onChanged={onNotesChanged}
        />
      ) : null}
      {isMobile ? (
        <WhatsappCallButton
          clientId={mission.client_id}
          name={missionName(mission)}
          phone={mission.phone}
        />
      ) : null}
      <div className="flex flex-wrap gap-2">
        {mission.phone && !isMobile ? (
          <Button
            asChild
            className="rounded-full bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-200"
          >
            <a href={`tel:${mission.phone}`}>
              <Phone className="h-4 w-4" />
              {mission.phone}
            </a>
          </Button>
        ) : null}
        <Button
          variant="outline"
          className="rounded-full"
          disabled={busy}
          onClick={() => onResult(mission, "no_answer")}
        >
          <PhoneMissed className="h-4 w-4" />
          No contesta
        </Button>
        <Button
          className="rounded-full bg-lime-400 text-neutral-900 hover:bg-lime-300"
          disabled={busy}
          onClick={() => onResult(mission, "contacted")}
        >
          <Check className="h-4 w-4" />
          Hablado
        </Button>
        {onOpenFicha ? (
          <Button
            variant="ghost"
            className={cn(
              "rounded-full",
              fichaOpen &&
                "bg-neutral-900 text-white hover:bg-neutral-800 hover:text-white dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-200 dark:hover:text-neutral-900",
            )}
            onClick={() => onOpenFicha(mission)}
          >
            <PanelRight className="h-4 w-4" />
            {fichaOpen ? "Ficha abierta" : "Ficha"}
          </Button>
        ) : (
          <Button asChild variant="ghost" className="rounded-full">
            <Link to={`/clientes?cliente=${mission.client_id}`}>
              <ExternalLink className="h-4 w-4" />
              Ficha
            </Link>
          </Button>
        )}
        <Button
          variant="ghost"
          className="ml-auto rounded-full text-neutral-500 dark:text-neutral-400"
          disabled={busy}
          onClick={() => onResult(mission, "skip")}
          title="Pasar a la siguiente sin contar llamada"
        >
          <SkipForward className="h-4 w-4" />
          Saltar
        </Button>
      </div>
      <p className="flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-500 dark:text-neutral-400">
        {onOpenFicha
          ? "¿Venta, recontacto o no compra? Márcalo en su ficha y se tacha solo."
          : "¿Venta, recontacto o no compra? Hazlo en su ficha y se tacha solo."}
        <span className="flex items-center gap-3">
          {onAssign ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onAssign(mission)}
              className="inline-flex items-center gap-1 hover:text-neutral-800 hover:underline disabled:opacity-40 dark:hover:text-neutral-100"
            >
              <UserPlus className="h-3 w-3" />
              Pasar a otra persona
            </button>
          ) : null}
          {onRelease ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onRelease(mission)}
              className="inline-flex items-center gap-1 hover:text-neutral-800 hover:underline disabled:opacity-40 dark:hover:text-neutral-100"
            >
              <X className="h-3 w-3" />
              Devolver a la bolsa
            </button>
          ) : null}
        </span>
      </p>
    </div>
  );
};

/** Admins: pass the lead to someone (Mica, a setter…) for a day. */
export const AssignButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-full p-1.5 text-neutral-600 hover:bg-white hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800"
    title="Pasar a otra persona"
  >
    <UserPlus className="h-4 w-4" />
  </button>
);

/** Note button for the compact rows: opens the client's notes underneath. */
export const NotesButton = ({
  count,
  open,
  onClick,
}: {
  count: number;
  open: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "relative rounded-full p-1.5 hover:bg-white dark:hover:bg-neutral-800",
      open || count
        ? "text-amber-700 dark:text-amber-400"
        : "text-neutral-700 dark:text-neutral-300",
      open && "bg-amber-100 dark:bg-amber-400/15",
    )}
    title={count ? `Notas (${count})` : "Escribir nota"}
  >
    <StickyNote className="h-4 w-4" />
    {count ? (
      <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold leading-none text-white">
        {count}
      </span>
    ) : null}
  </button>
);

/** Notes folded under a compact row; the latest one peeks out when closed. */
export const RowNotes = ({
  contactId,
  notes,
  open,
  onOpen,
  onChanged,
}: {
  contactId: CallMission["contact_id"];
  notes?: ContactNotes;
  open: boolean;
  onOpen: () => void;
  onChanged?: () => void;
}) => {
  if (!notes || !onChanged) return null;
  if (open) {
    return (
      <div className="px-1 pb-1">
        <ClientNotes
          contactId={contactId}
          notes={notes}
          onChanged={onChanged}
          startWriting={!notes.team.length}
        />
      </div>
    );
  }
  return notes.team.length ? (
    <NotePreview notes={notes} onClick={onOpen} />
  ) : null;
};

/** Compact row for the rest of the list. */
export const MissionRow = ({
  mission,
  busy,
  onResult,
  onRelease,
  notes,
  onNotesChanged,
  onOpenFicha,
  fichaOpen,
  onAssign,
}: {
  mission: CallMission;
  busy: boolean;
  onResult: (mission: CallMission, result: QuickResult) => void;
  onRelease: (mission: CallMission) => void;
  onAssign?: (mission: CallMission) => void;
  notes?: ContactNotes;
  onNotesChanged?: () => void;
  onOpenFicha?: (mission: CallMission) => void;
  fichaOpen?: boolean;
}) => {
  const [notesOpen, setNotesOpen] = useState(false);
  const isMobile = useIsMobile();
  return (
    <li
      className={cn(
        "flex flex-col gap-1.5 rounded-xl bg-white/70 px-3 py-2 dark:bg-neutral-900",
        fichaOpen && "ring-2 ring-neutral-900 dark:ring-lime-300",
      )}
    >
      <div className="flex items-center gap-2">
        <div className="flex min-w-0 flex-1 flex-col">
          {onOpenFicha ? (
            <button
              type="button"
              onClick={() => onOpenFicha(mission)}
              className="truncate text-left text-sm font-medium text-neutral-900 hover:underline dark:text-neutral-100"
              title="Abrir ficha"
            >
              {missionName(mission)}
            </button>
          ) : (
            <Link
              to={`/clientes?cliente=${mission.client_id}`}
              className="truncate text-sm font-medium text-neutral-900 hover:underline dark:text-neutral-100"
            >
              {missionName(mission)}
            </Link>
          )}
          <MissionDetails mission={mission} />
        </div>
        {notes && onNotesChanged ? (
          <NotesButton
            count={notes.team.length}
            open={notesOpen}
            onClick={() => setNotesOpen(!notesOpen)}
          />
        ) : null}
        {isMobile ? (
          <WhatsappCallButton
            clientId={mission.client_id}
            name={missionName(mission)}
            phone={mission.phone}
            variant="icon"
            className="p-1.5"
          />
        ) : mission.phone ? (
          <a
            href={`tel:${mission.phone}`}
            className="rounded-full p-1.5 text-neutral-700 hover:bg-white dark:text-neutral-300 dark:hover:bg-neutral-800"
            title={`Llamar ${mission.phone}`}
          >
            <Phone className="h-4 w-4" />
          </a>
        ) : null}
        <button
          type="button"
          disabled={busy}
          onClick={() => onResult(mission, "no_answer")}
          className="rounded-full p-1.5 text-neutral-700 hover:bg-white disabled:opacity-40 dark:text-neutral-300 dark:hover:bg-neutral-800"
          title="No contesta"
        >
          <PhoneMissed className="h-4 w-4" />
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onResult(mission, "contacted")}
          className="rounded-full p-1.5 text-neutral-700 hover:bg-white disabled:opacity-40 dark:text-neutral-300 dark:hover:bg-neutral-800"
          title="Hablado"
        >
          <Check className="h-4 w-4" />
        </button>
        {onAssign ? <AssignButton onClick={() => onAssign(mission)} /> : null}
        <button
          type="button"
          disabled={busy}
          onClick={() => onRelease(mission)}
          className="rounded-full p-1.5 text-neutral-400 hover:bg-white hover:text-neutral-700 disabled:opacity-40 dark:text-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
          title="Devolver a la bolsa"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <RowNotes
        contactId={mission.contact_id}
        notes={notes}
        open={notesOpen}
        onOpen={() => setNotesOpen(true)}
        onChanged={onNotesChanged}
      />
    </li>
  );
};
